import React, { useState, useEffect } from 'react';
import { Edit2, X, Save, ChevronDown, ChevronRight, Image as ImageIcon, Globe, Type, Layers } from 'lucide-react';
import { toast } from 'react-toastify';
import { translationApi } from '../api/api';

// Page structure definition
const PAGE_STRUCTURE = {
  whyAvopla: {
    name: 'Why Avopla',
    sections: [
      {
        id: 'header', name: '🖼️ Header / Hero', description: 'Main banner', fields: [
          { key: 'hero.title', label: 'Title', type: 'textarea' },
          { key: 'hero.registerButton', label: 'Register Button', type: 'input' },
          { key: 'hero.exploreButton', label: 'Explore Button', type: 'input' },
        ], images: ['whyAvopla-hero']
      },
      {
        id: 'winWin', name: '🤝 Win-Win Partnership', description: 'Red title and subtitle', fields: [
          { key: 'winWin.title', label: 'Title', type: 'input' },
          { key: 'winWin.subtitle', label: 'Subtitle', type: 'input' },
        ]
      },
      {
        id: 'powerful', name: '💪 How Avopla is Powerful', description: '4 feature cards', fields: [
          { key: 'powerful.title', label: 'Section Title', type: 'input' },
          { key: 'powerful.reachAudience.title', label: 'Card 1 Title', type: 'input' },
          { key: 'powerful.reachAudience.text', label: 'Card 1 Text', type: 'textarea' },
          { key: 'powerful.takeControl.title', label: 'Card 2 Title', type: 'input' },
          { key: 'powerful.takeControl.text', label: 'Card 2 Text', type: 'textarea' },
        ]
      },
      {
        id: 'footerCta', name: '📢 Footer CTA', description: 'Bottom banner', fields: [
          { key: 'footerCta.title', label: 'CTA Title', type: 'input' },
          { key: 'footerCta.subtitle', label: 'CTA Subtitle', type: 'input' },
          { key: 'footerCta.button', label: 'Button Text', type: 'input' },
        ]
      }
    ]
  },
  pricing: {
    name: 'Pricing',
    sections: [
      {
        id: 'header', name: '🖼️ Header', description: 'Title and subtitle', fields: [
          { key: 'hero.title', label: 'Title', type: 'input' },
          { key: 'hero.subtitle', label: 'Subtitle', type: 'input' },
          { key: 'hero.registerButton', label: 'Register Button', type: 'input' },
        ]
      },
      {
        id: 'plans', name: '💰 Plans Header', description: 'Plans section titles', fields: [
          { key: 'plans.title', label: 'Title', type: 'input' },
          { key: 'plans.subtitle', label: 'Subtitle', type: 'input' },
        ]
      },
      {
        id: 'basicPlan', name: '📦 Essential Plan', description: 'Basic plan features', fields: [
          { key: 'basic.title', label: 'Plan Title', type: 'input' },
          { key: 'basic.getStarted', label: 'Button Text', type: 'input' },
        ]
      },
      {
        id: 'faq', name: '❓ FAQ', description: 'Questions and answers', fields: [
          { key: 'faq.title', label: 'FAQ Title', type: 'input' },
          { key: 'faq.q1.question', label: 'Q1', type: 'input' },
          { key: 'faq.q1.answer', label: 'A1', type: 'textarea' },
        ]
      }
    ]
  },
  nav: {
    name: 'Navigation',
    sections: [
      {
        id: 'links', name: '🔗 Navigation Links', description: 'Top menu items', fields: [
          { key: 'whyAvopla', label: 'Why Avopla', type: 'input' },
          { key: 'pricing', label: 'Pricing', type: 'input' },
          { key: 'login', label: 'Login', type: 'input' },
        ]
      }
    ]
  },
  footer: {
    name: 'Footer',
    sections: [
      {
        id: 'links', name: '🔗 Footer Links', description: 'Footer navigation', fields: [
          { key: 'signIn', label: 'Sign In', type: 'input' },
          { key: 'privacyPolicy', label: 'Privacy Policy', type: 'input' },
          { key: 'termsOfUse', label: 'Terms of Use', type: 'input' },
          { key: 'copyright', label: 'Copyright', type: 'input' },
        ]
      }
    ]
  }
};

const SAMPLE_DATA = {
  whyAvopla: {
    'hero.title': { en: 'Connect with our dinner network', fr: 'Connectez-vous à notre réseau' },
    'hero.registerButton': { en: 'REGISTER RESTAURANT', fr: 'INSCRIRE LE RESTAURANT' },
  },
  nav: {
    'whyAvopla': { en: 'Why Avopla?', fr: 'Pourquoi Avopla?' },
    'pricing': { en: 'Pricing', fr: 'Tarification' },
  }
};

const PageEditor = () => {
  const [activePage, setActivePage] = useState('whyAvopla');
  const [expandedSections, setExpandedSections] = useState({});
  const [translations, setTranslations] = useState({});
  const [loading, setLoading] = useState(true);
  const [editingField, setEditingField] = useState(null);
  const [editValues, setEditValues] = useState({ en: '', fr: '' });

  useEffect(() => {
    fetchTranslations();
  }, []);

  const fetchTranslations = async () => {
    try {
      setLoading(true);
      const response = await translationApi.getAll();
      const grouped = {};
      response.data.forEach(t => {
        if (!grouped[t.section]) grouped[t.section] = {};
        grouped[t.section][t.key] = { en: t.en, fr: t.fr, id: t.id };
      });
      setTranslations(grouped);
    } catch (error) {
      setTranslations(SAMPLE_DATA);
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const expandAllSections = () => {
    const allExpanded = {};
    PAGE_STRUCTURE[activePage]?.sections.forEach(s => {
      allExpanded[s.id] = true;
    });
    setExpandedSections(allExpanded);
  };

  const startEditing = (pageKey, fieldKey, currentValue) => {
    setEditingField({ pageKey, fieldKey });
    setEditValues({
      en: currentValue?.en || '',
      fr: currentValue?.fr || ''
    });
  };

  const saveEdit = async () => {
    if (!editingField) return;
    try {
      const { pageKey, fieldKey } = editingField;
      const existing = translations[pageKey]?.[fieldKey];

      if (existing?.id) {
        await translationApi.update(existing.id, { en: editValues.en, fr: editValues.fr });
      } else {
        await translationApi.create({ section: pageKey, key: fieldKey, en: editValues.en, fr: editValues.fr });
      }
      toast.success('Saved!');
      setEditingField(null);
      fetchTranslations();
    } catch (error) {
      setTranslations(prev => ({
        ...prev,
        [editingField.pageKey]: {
          ...prev[editingField.pageKey],
          [editingField.fieldKey]: { en: editValues.en, fr: editValues.fr }
        }
      }));
      toast.success('Saved (demo)');
      setEditingField(null);
    }
  };

  const cancelEdit = () => {
    setEditingField(null);
    setEditValues({ en: '', fr: '' });
  };

  const getValue = (pageKey, fieldKey) => {
    return translations[pageKey]?.[fieldKey] || { en: '', fr: '' };
  };

  if (loading) {
    return (
      <div className="loading-overlay">
        <div className="spinner"></div>
      </div>
    );
  }

  const currentPage = PAGE_STRUCTURE[activePage];
  const totalFields = currentPage?.sections.reduce((acc, s) => acc + s.fields.length, 0) || 0;
  const totalSections = currentPage?.sections.length || 0;

  return (
    <div>
      {/* Header */}
      <div className="page-header">
        <h1>Page Editor</h1>
        <p>Edit translations organized by page sections</p>
      </div>

      {/* Page Tabs */}
      <div className="tabs">
        {Object.entries(PAGE_STRUCTURE).map(([key, page]) => (
          <button
            key={key}
            onClick={() => { setActivePage(key); setExpandedSections({}); }}
            className={`tab ${activePage === key ? 'active' : ''}`}
          >
            {page.name}
          </button>
        ))}
      </div>

      {/* Stats Bar */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '24px',
        marginBottom: '20px',
        padding: '14px 18px',
        background: 'var(--bg-secondary)',
        borderRadius: 'var(--radius-lg)',
        border: '1px solid var(--border)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Globe size={16} style={{ color: 'var(--text-tertiary)' }} />
          <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
            <strong style={{ color: 'var(--text-primary)' }}>{totalSections}</strong> sections
          </span>
        </div>
        <div style={{ width: '1px', height: '16px', background: 'var(--border)' }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Type size={16} style={{ color: 'var(--text-tertiary)' }} />
          <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
            <strong style={{ color: 'var(--text-primary)' }}>{totalFields}</strong> fields
          </span>
        </div>
        <div style={{ marginLeft: 'auto' }}>
          <button className="btn btn-secondary btn-sm" onClick={expandAllSections}>
            <ChevronDown size={14} />
            Expand All
          </button>
        </div>
      </div>

      {/* Sections */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {currentPage?.sections.map(section => (
          <div key={section.id} className="card">
            {/* Section Header */}
            <div
              onClick={() => toggleSection(section.id)}
              style={{
                padding: '16px 20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
                background: expandedSections[section.id] ? 'var(--bg-tertiary)' : 'transparent',
                borderBottom: expandedSections[section.id] ? '1px solid var(--border)' : 'none',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: 'var(--radius)',
                  background: expandedSections[section.id] ? 'var(--accent)' : 'var(--bg-hover)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  {expandedSections[section.id]
                    ? <ChevronDown size={16} style={{ color: 'white' }} />
                    : <ChevronRight size={16} style={{ color: 'var(--text-tertiary)' }} />
                  }
                </div>
                <div>
                  <h3 style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-primary)', margin: 0 }}>
                    {section.name}
                  </h3>
                  <p style={{ fontSize: '12px', color: 'var(--text-tertiary)', margin: '2px 0 0 0' }}>
                    {section.description}
                  </p>
                </div>
              </div>
              <span className="badge badge-gray">{section.fields.length} fields</span>
            </div>

            {/* Section Content */}
            {expandedSections[section.id] && (
              <div style={{ padding: '20px' }}>
                {/* Images Reference */}
                {section.images && section.images.length > 0 && (
                  <div className="alert alert-warning" style={{ marginBottom: '16px' }}>
                    <ImageIcon size={16} className="alert-icon" />
                    <div className="alert-content">
                      <h4>Section Images</h4>
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '8px' }}>
                        {section.images.map(img => (
                          <code key={img} style={{ fontSize: '12px', padding: '4px 8px', background: 'var(--bg-tertiary)', borderRadius: 'var(--radius-sm)' }}>
                            {img}
                          </code>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Fields */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {section.fields.map(field => {
                    const value = getValue(activePage, field.key);
                    const isEditing = editingField?.pageKey === activePage && editingField?.fieldKey === field.key;

                    return (
                      <div
                        key={field.key}
                        style={{
                          padding: '16px',
                          background: isEditing ? 'var(--accent-subtle)' : 'var(--bg-primary)',
                          borderRadius: 'var(--radius-md)',
                          border: isEditing ? '2px solid var(--accent)' : '1px solid var(--border)',
                        }}
                      >
                        {/* Field Header */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                          <div>
                            <div style={{ fontWeight: '600', fontSize: '13px', color: 'var(--text-primary)', marginBottom: '4px' }}>
                              {field.label}
                            </div>
                            <code style={{ fontSize: '11px', color: 'var(--accent)', background: 'var(--bg-tertiary)', padding: '2px 6px', borderRadius: 'var(--radius-sm)' }}>
                              {field.key}
                            </code>
                          </div>
                          {!isEditing && (
                            <button className="btn btn-outline btn-sm" onClick={() => startEditing(activePage, field.key, value)}>
                              <Edit2 size={13} />
                              Edit
                            </button>
                          )}
                        </div>

                        {isEditing ? (
                          /* Edit Mode */
                          <div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                              <div className="form-group" style={{ marginBottom: 0 }}>
                                <label className="form-label">🇬🇧 English</label>
                                {field.type === 'textarea' ? (
                                  <textarea
                                    className="form-textarea"
                                    value={editValues.en}
                                    onChange={e => setEditValues({ ...editValues, en: e.target.value })}
                                    rows={3}
                                  />
                                ) : (
                                  <input
                                    type="text"
                                    className="form-input"
                                    value={editValues.en}
                                    onChange={e => setEditValues({ ...editValues, en: e.target.value })}
                                  />
                                )}
                              </div>
                              <div className="form-group" style={{ marginBottom: 0 }}>
                                <label className="form-label">🇫🇷 French</label>
                                {field.type === 'textarea' ? (
                                  <textarea
                                    className="form-textarea"
                                    value={editValues.fr}
                                    onChange={e => setEditValues({ ...editValues, fr: e.target.value })}
                                    rows={3}
                                  />
                                ) : (
                                  <input
                                    type="text"
                                    className="form-input"
                                    value={editValues.fr}
                                    onChange={e => setEditValues({ ...editValues, fr: e.target.value })}
                                  />
                                )}
                              </div>
                            </div>
                            <div style={{ display: 'flex', gap: '8px', marginTop: '12px', justifyContent: 'flex-end' }}>
                              <button className="btn btn-secondary btn-sm" onClick={cancelEdit}>
                                <X size={14} />
                                Cancel
                              </button>
                              <button className="btn btn-primary btn-sm" onClick={saveEdit}>
                                <Save size={14} />
                                Save
                              </button>
                            </div>
                          </div>
                        ) : (
                          /* View Mode */
                          <div className="translation-languages">
                            <div className="translation-lang">
                              <div className="translation-lang-header">
                                <span className="translation-lang-flag">🇬🇧</span>
                                English
                              </div>
                              <div className={`translation-lang-text ${!value.en ? 'translation-lang-empty' : ''}`}>
                                {value.en || 'Not set'}
                              </div>
                            </div>
                            <div className="translation-lang">
                              <div className="translation-lang-header">
                                <span className="translation-lang-flag">🇫🇷</span>
                                French
                              </div>
                              <div className={`translation-lang-text ${!value.fr ? 'translation-lang-empty' : ''}`}>
                                {value.fr || 'Not set'}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default PageEditor;
